/* eslint-disable no-param-reassign */
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as WordCloudLayerSpec } from '../../../../layer/preset/other/wordCloud';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface WordCloudLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type WordCloudLayerType = StandardChartLayerSpec &
  WordCloudLayerSpec &
  WordCloudLayerTypeWithFields;

// 创建词云图数据类型验证器
const validateWordCloudDataTypes = createDataTypeValidator('wordCloud', [
  { field: 'x', type: 'string' },
  { field: 'y', type: 'number' }
]);

// 创建词云图编码验证器
const validateWordCloudEncoding = createEncodingValidator([{ field: 'x' }, { field: 'y' }]);

/**
 * 验证颜色配置
 * @param layer 图层配置
 * @returns 配置是否有效
 */
function validateColors(layer: WordCloudLayerType): boolean {
  const { dvTextColors } = layer;

  // 如果没有提供自定义颜色，则使用默认颜色，不需要验证
  if (!dvTextColors) {
    return true;
  }

  // 检查颜色数组是否有至少7个颜色（对应7个级别）
  if (dvTextColors.length < 7) {
    utils.logger.warn(
      'warning',
      `The "dvTextColors" array should contain at least 7 colors for all levels in "${layer.type}" layer`
    );
    return false;
  }

  return true;
}

/**
 * 记录范围属性错误
 */
function logRangeError(layer: WordCloudLayerType, propName: string): void {
  utils.logger.warn(
    'warning',
    `Invalid "${propName}" in "${layer.type}" layer. It should be a 2-element array [min, max] where min < max`
  );
}

/**
 * 验证数值范围配置是否有效
 * @param layer 图层配置
 * @param propName 属性名称
 * @returns 是否有效
 */
function validateRangeProperty(
  layer: WordCloudLayerType,
  propName: 'dvFontSizeRange' | 'dvFontWeightRange'
): boolean {
  const rangeValue = layer[propName];

  if (!rangeValue) {
    return true;
  }

  // 分步检查，避免表达式过于复杂
  if (!Array.isArray(rangeValue)) {
    logRangeError(layer, propName);
    return false;
  }

  if (rangeValue.length !== 2) {
    logRangeError(layer, propName);
    return false;
  }

  if (typeof rangeValue[0] !== 'number' || typeof rangeValue[1] !== 'number') {
    logRangeError(layer, propName);
    return false;
  }

  if (rangeValue[0] >= rangeValue[1]) {
    logRangeError(layer, propName);
    return false;
  }

  return true;
}

// 额外的检查函数
const additionalWordCloudChecks = (layer: WordCloudLayerType) => {
  let isValid = true;

  // 验证颜色配置
  if (!validateColors(layer)) {
    isValid = false;
    utils.logger.warn('warning', `Color configuration check failed for "${layer.type}" layer`);
  }

  // 验证字体大小范围
  if (!validateRangeProperty(layer, 'dvFontSizeRange')) {
    isValid = false;
  }

  // 验证字体粗细范围
  if (!validateRangeProperty(layer, 'dvFontWeightRange')) {
    isValid = false;
  }

  if (!isValid) {
    layer.__isEncodingValid = false;
  }
};

// 创建并导出词云图处理器
export const wordCloud = createLayerProcessor<WordCloudLayerType>(
  'wordCloud',
  validateWordCloudEncoding,
  validateWordCloudDataTypes,
  additionalWordCloudChecks
);
